/**
 * ACT-1b — page-visit recorder for My Last Activity.
 *
 * Mounted once in the authenticated app shell. After each route change
 * settles (~1.5 s without another navigation) it posts one visit:
 * `{path, title, section}`. Fire and forget — failures are swallowed and
 * rendering is never blocked. The server sanitises the path (sensitive
 * query parameters), applies the exclusions again and dedupes.
 *
 * Cross-References:
 *   - m4-specifications/my-last-activity-design.md §2, §4, §5 (Recording)
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { navigationSnapshot, type NavItem } from '@/hooks/useNavigation';

export const VISIT_SETTLE_MS = 1500;

/** Sign-in/auth routes and the activity pages themselves are never recorded. */
const EXCLUDED_PATHS: readonly RegExp[] = [
  /^\/login(?:\/|$)/,
  /^\/internal\/login(?:\/|$)/,
  /^\/auth(?:\/|$)/,
  /^\/activity(?:\/|$)/,
  /^\/admin\/users\/[^/]+\/activity\/?$/,
];

export function isExcludedVisitPath(pathname: string): boolean {
  return EXCLUDED_PATHS.some((pattern) => pattern.test(pathname));
}

/** Longest nav `path` that equals the pathname or is a segment prefix of it. */
export function bestNavItem(items: readonly NavItem[], pathname: string): NavItem | undefined {
  let best: NavItem | undefined;
  for (const item of items) {
    const base = item.path.replace(/\/+$/, '');
    const matches = base === '' ? pathname === '/' : pathname === base || pathname.startsWith(`${base}/`);
    if (matches && (!best || base.length > best.path.replace(/\/+$/, '').length)) best = item;
  }
  return best;
}

/**
 * The label the sidebar shows for the item's section: the module's own
 * section heading for module pages (and module-contributed admin rows),
 * otherwise "Administration" or "Main".
 */
export function navSectionLabel(item: NavItem): string {
  if (item.module_name) return item.module_name;
  if (item.section === 'admin') return 'Administration';
  if (item.section === 'modules') return 'Modules';
  return 'Main';
}

/** First rendered `main h1`, ignoring a page header that is still loading. */
function mainHeading(): string | null {
  const heading = document.querySelector('main h1');
  if (!heading || heading.closest('[aria-busy="true"]')) return null;
  const text = heading.textContent?.replace(/\s+/g, ' ').trim();
  return text || null;
}

export function useVisitRecorder(): void {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (isExcludedVisitPath(pathname)) return;
    const timer = window.setTimeout(() => {
      const match = bestNavItem(navigationSnapshot(), pathname);
      const title = mainHeading() ?? match?.label ?? null;
      const section = match ? navSectionLabel(match) : null;
      const body: { path: string; title?: string; section?: string } = { path: `${pathname}${search}` };
      if (title) body.title = title;
      if (section) body.section = section;
      try {
        apiFetch('/me/activity/visits', { method: 'POST', body: JSON.stringify(body) })
          .catch(() => { /* fire and forget */ });
      } catch {
        /* fire and forget */
      }
    }, VISIT_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [pathname, search]);
}
